import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customFetch } from '../apiClient';

const PharmaCompanyForm = () => {
  const [form, setForm] = useState({
    companyName: "",
    address: "",
    landmark: "",
    state: "",
    district: "",
    pincode: "",
    contact1: "",
    contact2: "",
    email: "",
    website: "",
    regNo: "",
    slogan1: "",
    slogan2: "",
    stateCode: "",
    msgSend: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [touched, setTouched] = useState({});

  // Modal Edit States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCompanies();
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []);

  const loadCompanies = async () => {
    try {
    setLoadingList(true);
      const res = await customFetch('/PharmaCompany');
      if (res.ok) setCompanies(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); 
    setSuccess("");

    if (!form.companyName?.trim()) {
      setError("Company Name is required.");
      setTouched({ companyName: true });
      return;
    }

    setLoading(true);
    try {
    
      const formData = new FormData();
      
      formData.append("Company_Name", form.companyName.trim());
      formData.append("Address", form.address || "");
      formData.append("Landmark", form.landmark || "");
      formData.append("State", form.state || "");
      formData.append("District", form.district || "");
      formData.append("Pincode", form.pincode || "");
      formData.append("Contact1", form.contact1 || "");
      formData.append("Contact2", form.contact2 || "");
      formData.append("Email_ID", form.email || "");
      formData.append("Website", form.website || "");
      formData.append("Reg_No", form.regNo || "");
      formData.append("Slogan1", form.slogan1 || "");
      formData.append("Slogan2", form.slogan2 || "");
      formData.append("StateCode", form.stateCode || "");
      formData.append("MsgSend", form.msgSend.toString());

      const fileInput = fileInputRef.current;
      if (fileInput?.files[0]) {
        formData.append("LogoFile", fileInput.files[0]);
      }

     const res = await customFetch('/PharmaCompany', {
        method: "POST",
        body: formData
      });

      if (!res.ok) throw new Error(await res.text());

      setSuccess("Pharma Company added successfully!");
      setForm({
        companyName: "",
        address: "",
        landmark: "",
        state: "",
        district: "",
        pincode: "",
        contact1: "",
        contact2: "",
        email: "",
        website: "",
        regNo: "",
        slogan1: "",
        slogan2: "",
        stateCode: "",
        msgSend: false
      });
      setLogoPreview(null);
      setTouched({});
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadCompanies();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (company) => {
    setEditCompany(company);
    setEditForm({
      companyName: company.company_Name || "",
      address: company.address || "",
      landmark: company.landmark || "",
      state: company.state || "",
      district: company.district || "",
      pincode: company.pincode || "",
      contact1: company.contact1 || "",
      contact2: company.contact2 || "",
      email: company.email_ID || "",
      website: company.website || "",
      regNo: company.reg_No || "",
      slogan1: company.slogan1 || "",
      slogan2: company.slogan2 || "",
      stateCode: company.stateCode || "",
      msgSend: company.msgSend || false
    });
    setEditLogoPreview(company.logoBase64 ? `data:image/jpeg;base64,${company.logoBase64}` : null);
    setEditError("");
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditCompany(null);
    setEditForm({});
    setEditLogoPreview(null);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
    setEditError("");
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.companyName?.trim()) {
      setEditError("Company Name is required");
      return;
    }

    setEditLoading(true);
    try {
   
      const formData = new FormData();
      
      formData.append("Company_Name", editForm.companyName.trim());
      formData.append("Address", editForm.address || "");
      formData.append("Landmark", editForm.landmark || "");
      formData.append("State", editForm.state || "");
      formData.append("District", editForm.district || "");
      formData.append("Pincode", editForm.pincode || "");
      formData.append("Contact1", editForm.contact1 || "");
      formData.append("Contact2", editForm.contact2 || "");
      formData.append("Email_ID", editForm.email || "");
      formData.append("Website", editForm.website || "");
      formData.append("Reg_No", editForm.regNo || "");
      formData.append("Slogan1", editForm.slogan1 || "");
      formData.append("Slogan2", editForm.slogan2 || "");
      formData.append("StateCode", editForm.stateCode || "");
      formData.append("MsgSend", editForm.msgSend.toString());

      const fileInput = editFileInputRef.current;
      if (fileInput?.files[0]) {
        formData.append("LogoFile", fileInput.files[0]);
      }

     const res = await customFetch(`/PharmaCompany/${editCompany.company_ID}`, {
        method: "PUT",
        body: formData
      });

      if (!res.ok) throw new Error(await res.text());

      loadCompanies();
      setSuccess("Pharma Company updated successfully!");
      closeEditModal();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const deleteCompany = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Pharma Company?")) return;

    try {
    const res = await customFetch(`/PharmaCompany/${id}`, {
        method: "DELETE"
      });

      if (!res.ok) throw new Error(await res.text());

      setCompanies(prev => prev.filter(c => c.company_ID !== id));
      setSuccess("Company deleted successfully!");
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleButtonClick = () => {
    navigate('/PharmaCompanyList');
  };

  const handleReset = () => {
    setForm({
      companyName: "",
      address: "",
      landmark: "",
      state: "",
      district: "",
      pincode: "",
      contact1: "",
      contact2: "",
      email: "",
      website: "",
      regNo: "",
      slogan1: "",
      slogan2: "",
      stateCode: "",
      msgSend: false
    });
    setLogoPreview(null);
    setTouched({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const filteredCompanies = companies.filter(company =>
    company.company_Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.state?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.district?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.contact1?.includes(searchQuery) ||
    company.reg_No?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="pharma-company-container">
      <div className="pharma-company-card">
        {/* Header */}
        <div className="company-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-hospital"></i>
            </div>
            <div className="header-text">
              <h5>Pharma Company Management</h5>
              <p>Manage pharmaceutical company details and information</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-view-list" onClick={handleButtonClick} title="View All Companies">
              <i className="bi bi-list-ul"></i>
              Companies List
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className="company-body">
          {/* Add Form */}
          <div className="form-section">
            <div className="section-header">
              <i className="bi bi-plus-circle"></i>
              <span>Add New Pharma Company</span>
            </div>
            <form ref={formRef} onSubmit={handleSubmit} className="company-form">
              {/* Basic Info */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <i className="bi bi-building"></i>
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    className={`form-control-custom ${touched.companyName && !form.companyName ? 'error' : ''}`}
                    tabIndex={1}
                    disabled={loading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-card-list"></i>
                    DL Number
                  </label>
                  <input 
                    name="regNo" 
                    value={form.regNo} 
                    onChange={handleChange} 
                    placeholder="PHARMA123456"
                    className="form-control-custom"
                    tabIndex={2}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-image"></i>
                    Company Logo
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    className="form-control-custom"
                    tabIndex={3}
                    disabled={loading}
                  />
                  {logoPreview && (
                    <img src={logoPreview} alt="Preview" className="logo-preview" />
                  )}
                </div>
              </div>

              {/* Address Info */}
              <div className="form-row">
                <div className="form-group flex-2">
                  <label>
                    <i className="bi bi-geo-alt"></i>
                    Address
                  </label>
                  <input 
                    name="address" 
                    value={form.address} 
                    onChange={handleChange} 
                    placeholder="Full address"
                    className="form-control-custom"
                    tabIndex={4}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-signpost"></i>
                    Landmark
                  </label>
                  <input 
                    name="landmark" 
                    value={form.landmark} 
                    onChange={handleChange} 
                    placeholder="Near XYZ Mall"
                    className="form-control-custom"
                    tabIndex={5}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-phone"></i>
                    Contact 1
                  </label>
                  <input 
                    name="contact1" 
                    value={form.contact1} 
                    onChange={handleChange} 
                    placeholder="+91 98XXXXXXXX"
                    className="form-control-custom"
                    tabIndex={6}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Location & Contact */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <i className="bi bi-map"></i>
                    State
                  </label>
                  <input 
                    name="state" 
                    value={form.state} 
                    onChange={handleChange} 
                    placeholder="Maharashtra"
                    className="form-control-custom"
                    tabIndex={7}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-pin-map"></i>
                    District
                  </label>
                  <input 
                    name="district" 
                    value={form.district} 
                    onChange={handleChange} 
                    placeholder="Pune"
                    className="form-control-custom"
                    tabIndex={8}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-mailbox"></i>
                    Pincode
                  </label>
                  <input 
                    name="pincode" 
                    value={form.pincode} 
                    onChange={handleChange} 
                    placeholder="411001"
                    className="form-control-custom"
                    tabIndex={9}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-telephone"></i>
                    Contact 2
                  </label>
                  <input 
                    name="contact2" 
                    value={form.contact2} 
                    onChange={handleChange} 
                    placeholder="+91 99XXXXXXXX"
                    className="form-control-custom"
                    tabIndex={10}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Email & Web */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <i className="bi bi-envelope"></i>
                    Email
                  </label>
                  <input 
                    name="email" 
                    type="email" 
                    value={form.email} 
                    onChange={handleChange} 
                    placeholder="company@pharma.com"
                    className="form-control-custom"
                    tabIndex={11}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-globe"></i>
                    Website
                  </label>
                  <input 
                    name="website" 
                    value={form.website} 
                    onChange={handleChange} 
                    placeholder="www.pharmacompany.com"
                    className="form-control-custom"
                    tabIndex={12}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-code-square"></i>
                    State Code
                  </label>
                  <input 
                    name="stateCode" 
                    value={form.stateCode} 
                    onChange={handleChange} 
                    placeholder="MH"
                    className="form-control-custom"
                    tabIndex={13}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <i className="bi bi-chat-quote"></i>
                    Slogan 1
                  </label>
                  <input 
                    name="slogan1" 
                    value={form.slogan1} 
                    onChange={handleChange} 
                    placeholder="Quality First"
                    className="form-control-custom"
                    tabIndex={14}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Slogans & Checkbox */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <i className="bi bi-chat-text"></i>
                    Slogan 2
                  </label>
                  <input 
                    name="slogan2" 
                    value={form.slogan2} 
                    onChange={handleChange} 
                    placeholder="Trust in Medicines"
                    className="form-control-custom"
                    tabIndex={15}
                    disabled={loading}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      name="msgSend"
                      checked={form.msgSend}
                      onChange={handleChange}
                      tabIndex={16}
                      disabled={loading}
                    />
                    <span>
                      <i className="bi bi-send-check"></i>
                      Allow Messages
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-reset" onClick={handleReset} disabled={loading}>
                  <i className="bi bi-arrow-clockwise"></i>
                  Reset
                </button>
                <button type="submit" className="btn-submit" disabled={loading} tabIndex={17}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i>
                      Add Company
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Companies List */}
          <div className="list-section">
            <div className="list-header">
              <div className="list-title">
                <i className="bi bi-list-ul"></i>
                <span>Companies List</span>
                <span className="badge-count">{filteredCompanies.length}</span>
              </div>
              <div className="toolbar-actions">
                <div className="search-box">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder="Search by name, location, DL number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button className="btn-refresh" onClick={loadCompanies} disabled={loadingList}>
                  <i className={`bi bi-arrow-clockwise ${loadingList ? 'spinning' : ''}`}></i>
                </button>
              </div>
            </div>

            <div className="table-container">
              {loadingList ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading companies...</p>
                </div>
              ) : filteredCompanies.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-inbox"></i>
                  <h6>No Companies Found</h6>
                  <p>{searchQuery ? "No companies match your search" : "No companies added yet"}</p>
                </div>
              ) : (
                <table className="companies-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>
                        <i className="bi bi-hash"></i> Sr
                      </th>
                      <th style={{ width: '80px' }}>Logo</th>
                      <th>Company Name</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>DL Number</th>
                      <th style={{ width: '120px' }}>
                        <i className="bi bi-tools"></i> Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((company, index) => (
                      <tr key={company.company_ID}>
                        <td>
                          <span className="sr-badge">{index + 1}</span>
                        </td>
                        <td>
                          {company.logoBase64 ? (
                            <img 
                              src={`data:image/jpeg;base64,${company.logoBase64}`} 
                              alt="Logo" 
                              className="company-logo"
                            />
                          ) : (
                            <div className="no-logo">
                              <i className="bi bi-image"></i>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="company-name">
                            <i className="bi bi-building-check"></i>
                            {company.company_Name}
                          </div>
                        </td>
                        <td>
                          <div className="contact-info">
                            {company.contact1 || company.contact2 ? (
                              <>
                                <i className="bi bi-telephone-fill"></i>
                                {company.contact1 || company.contact2}
                              </>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="location-info">
                            <i className="bi bi-geo-alt-fill"></i>
                            {company.state}{company.district ? `, ${company.district}` : ""}
                          </div>
                        </td>
                        <td>
                          {company.reg_No ? (
                            <span className="dl-badge">
                              <i className="bi bi-card-checklist"></i>
                              {company.reg_No}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => openEditModal(company)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => deleteCompany(company.company_ID)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <>
          <div className="modal-backdrop" onClick={closeEditModal}></div>
          <div className="modal-dialog-custom">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h5>
                  <i className="bi bi-pencil-square"></i>
                  Edit Pharma Company
                </h5>
                <button className="btn-close-modal" onClick={closeEditModal}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={saveEdit}>
                <div className="modal-body-custom">
                  {editError && (
                    <div className="alert-custom alert-danger">
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <span>{editError}</span>
                    </div>
                  )}
                  
                  {/* Same form structure as add form */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <i className="bi bi-building"></i>
                        Company Name <span className="required">*</span>
                      </label>
                      <input
                        name="companyName"
                        value={editForm.companyName}
                        onChange={handleEditChange}
                        className="form-control-custom"
                        required
                        disabled={editLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <i className="bi bi-card-list"></i>
                        DL Number
                      </label>
                      <input 
                        name="regNo" 
                        value={editForm.regNo} 
                        onChange={handleEditChange} 
                        className="form-control-custom"
                        disabled={editLoading}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <i className="bi bi-image"></i>
                        Company Logo
                      </label>
                      <input
                        type="file"
                        ref={editFileInputRef}
                        onChange={handleEditLogoChange}
                        accept="image/*"
                        className="form-control-custom"
                        disabled={editLoading}
                      />
                      {editLogoPreview && (
                        <img src={editLogoPreview} alt="Preview" className="logo-preview" />
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label><i className="bi bi-geo-alt"></i> Address</label>
                      <input name="address" value={editForm.address} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-signpost"></i> Landmark</label>
                      <input name="landmark" value={editForm.landmark} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-phone"></i> Contact 1</label>
                      <input name="contact1" value={editForm.contact1} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label><i className="bi bi-map"></i> State</label>
                      <input name="state" value={editForm.state} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-pin-map"></i> District</label>
                      <input name="district" value={editForm.district} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-mailbox"></i> Pincode</label>
                      <input name="pincode" value={editForm.pincode} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-telephone"></i> Contact 2</label>
                      <input name="contact2" value={editForm.contact2} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label><i className="bi bi-envelope"></i> Email</label>
                      <input name="email" type="email" value={editForm.email} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-globe"></i> Website</label>
                      <input name="website" value={editForm.website} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-code-square"></i> State Code</label>
                      <input name="stateCode" value={editForm.stateCode} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group">
                      <label><i className="bi bi-chat-quote"></i> Slogan 1</label>
                      <input name="slogan1" value={editForm.slogan1} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label><i className="bi bi-chat-text"></i> Slogan 2</label>
                      <input name="slogan2" value={editForm.slogan2} onChange={handleEditChange} 
                        className="form-control-custom" disabled={editLoading} />
                    </div>
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          name="msgSend"
                          checked={editForm.msgSend}
                          onChange={handleEditChange}
                          disabled={editLoading}
                        />
                        <span>
                          <i className="bi bi-send-check"></i>
                          Allow Messages
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer-custom">
                  <button type="button" className="btn-cancel" onClick={closeEditModal} disabled={editLoading}>
                    <i className="bi bi-x-circle"></i>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save" disabled={editLoading}>
                    {editLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle"></i>
                        Update Company
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Styles */}
     <style jsx>{`
        .pharma-company-container {
          padding: 0;
        }

        .pharma-company-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 15px rgba(0, 136, 204, 0.08);
          border: 1px solid #e0e6ed;
          overflow: hidden;
        }

        /* Animations */
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .slide-in {
          animation: slideIn 0.3s ease;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Header - SOLID COLOR */
        .company-header {
          /* Fallback */
          background: #0088cc;
          /* Dynamic Theme Color */
          background: var(--theme-color, #0088cc);
          
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 3px solid #00d4aa;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-icon {
          width: 45px;
          height: 45px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
          animation: pulse 2s ease-in-out infinite;
        }

        .header-text h5 {
          margin: 0;
          color: white;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.2;
        }

        .header-text p {
          margin: 0;
          color: rgba(255, 255, 255, 0.85);
          font-size: 12px;
        }

        .btn-view-list {
          padding: 8px 16px;
          border-radius: 8px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15);
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          backdrop-filter: blur(10px);
        }

        .btn-view-list:hover {
          background: white;
          /* Dynamic Text */
          color: var(--theme-color, #0088cc);
          border-color: white;
          transform: translateY(-2px);
        }

        /* Alerts - SOLID COLORS */
        .alert-custom {
          margin: 15px 20px 0;
          padding: 12px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 500;
          position: relative;
        }

        .alert-danger {
          background: #fee;
          color: #c33;
          border: 1px solid #fcc;
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-custom i {
          font-size: 16px;
        }

        .alert-close {
          position: absolute;
          right: 10px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: inherit;
          opacity: 0.6;
          transition: opacity 0.2s;
        }

        .alert-close:hover {
          opacity: 1;
        }

        /* Body */
        .company-body {
          padding: 15px 20px;
        }

        /* Form Section */
        .form-section {
          background: #ffffff;
          border: 1px solid #e0e6ed;
          border-radius: 8px;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .section-header {
          background: #f0f4f8; /* Solid Light Grey */
          padding: 10px 15px;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 2px solid var(--theme-color, #0088cc); /* Dynamic Border */
        }

        .section-header i {
          font-size: 16px;
          color: var(--theme-color, #0088cc);
        }

        .section-header span {
          font-size: 14px;
          font-weight: 600;
          color: #2c3e50;
        }

        /* Company Form */
        .company-form {
          padding: 15px;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 12px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.flex-2 {
          grid-column: span 2;
        }

        .form-group.checkbox-group {
          justify-content: flex-end;
        }

        .form-group label {
          font-size: 12px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .form-group label i {
          color: var(--theme-color, #0088cc);
        }

        .required {
          color: #e74c3c;
        }

        .form-control-custom {
          padding: 8px 12px;
          border: 2px solid #cbd5e0;
          border-radius: 6px;
          font-size: 12px;
          color: #2c3e50;
          transition: all 0.3s ease;
          outline: none;
          background: white;
        }

        /* Dynamic Focus Border */
        .form-control-custom:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
        }

        .form-control-custom.error {
          border-color: #e74c3c;
          background: #fff5f5;
        }

        .logo-preview {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 6px;
          margin-top: 8px;
          border: 2px solid #e0e6ed;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          margin-bottom: 0;
        }

        .checkbox-label span {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .form-check-input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        /* Form Actions */
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 15px;
          padding-top: 15px;
          border-top: 2px solid #e0e6ed;
        }

        .btn-reset, .btn-submit {
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-reset {
          background: #e0e6ed;
          color: #2c3e50;
        }

        .btn-reset:hover:not(:disabled) {
          background: #cbd5e0;
          transform: translateY(-2px);
        }

        /* Submit Button - SOLID COLOR */
        .btn-submit {
          /* Dynamic BG */
          background: var(--theme-color, #0088cc);
          color: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          opacity: 0.9;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-reset:disabled, .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* List Section */
        .list-section {
          background: white;
          border: 1px solid #e0e6ed;
          border-radius: 8px;
          overflow: hidden;
        }

        .list-header {
          background: #f0f4f8; /* Solid Grey */
          padding: 12px 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          /* Dynamic Bottom Border */
          border-bottom: 2px solid var(--theme-color, #0088cc);
        }

        .list-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #2c3e50;
        }

        .list-title i {
          font-size: 16px;
          color: var(--theme-color, #0088cc);
        }

        /* Badge Count - SOLID COLOR */
        .badge-count {
          background: var(--theme-color, #0088cc);
          color: white;
          padding: 3px 10px;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 700;
        }

        .toolbar-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .search-box {
          position: relative;
          width: 250px;
        }

        .search-box i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #7f8c8d;
          font-size: 12px;
        }

        .search-box input {
          width: 100%;
          padding: 6px 10px 6px 32px;
          border: 1px solid #cbd5e0;
          border-radius: 50px;
          font-size: 11px;
          outline: none;
          transition: all 0.3s ease;
        }

        /* Search Focus */
        .search-box input:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
        }

        .btn-refresh {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          /* Dynamic Border & Text */
          border: 1px solid var(--theme-color, #0088cc);
          background: white;
          color: var(--theme-color, #0088cc);
          
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-refresh:hover:not(:disabled) {
          /* Dynamic Hover BG */
          background: var(--theme-color, #0088cc);
          color: white;
          transform: translateY(-2px);
        }

        .btn-refresh:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Loading & Empty States */
        .loading-state, .empty-state {
          text-align: center;
          padding: 50px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e0e6ed;
          /* Dynamic Spinner Color */
          border-top-color: var(--theme-color, #0088cc);
          
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        .loading-state p, .empty-state p {
          color: #7f8c8d;
          font-size: 13px;
          margin: 0;
        }

        .empty-state i {
          font-size: 56px;
          color: #cbd5e0;
          margin-bottom: 12px;
        }

        .empty-state h6 {
          margin: 0 0 6px 0;
          color: #2c3e50;
          font-size: 16px;
          font-weight: 600;
        }

        /* Table */
        .table-container {
          overflow-x: auto;
        }

        .companies-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .companies-table thead {
          /* Dynamic Header BG */
          background: var(--theme-color, #0088cc);
        }

        .companies-table thead th {
          padding: 12px 14px;
          text-align: left;
          font-weight: 600;
          color: white;
          font-size: 11px;
          border-bottom: 2px solid #00d4aa;
          white-space: nowrap;
        }

        .companies-table thead th i {
          margin-right: 6px;
        }

        .companies-table tbody tr {
          border-bottom: 1px solid #f0f4f8;
          transition: all 0.2s ease;
        }

        .companies-table tbody tr:hover {
          background: #f8f9fa;
        }

        .companies-table tbody td {
          padding: 10px 14px;
          color: #2c3e50;
          vertical-align: middle;
        }

        /* SR Badge - SOLID COLOR */
        .sr-badge {
          /* Dynamic BG */
          background: var(--theme-color, #0088cc);
          color: white;
          padding: 4px 10px;
          border-radius: 5px;
          font-weight: 600;
          font-size: 11px;
          display: inline-block;
        }

        .company-logo {
          width: 50px;
          height: 50px;
          object-fit: contain;
          border-radius: 8px;
          border: 2px solid #e0e6ed;
          padding: 4px;
          background: white;
        }

        .no-logo {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          border: 2px dashed #cbd5e0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a0aec0;
          background: #f8f9fa;
        }

        .company-name, .contact-info, .location-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
        }

        /* Dynamic Icons in Table */
        .company-name i {
          color: var(--theme-color, #0088cc);
          font-size: 16px;
        }

        .contact-info i {
          color: #f39c12;
        }

        .location-info i {
          color: #27ae60;
        }

        .dl-badge {
          background: #e7f3ff;
          /* Dynamic Text */
          color: var(--theme-color, #0088cc);
          
          padding: 4px 10px;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 6px;
          justify-content: center;
        }

        .btn-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .btn-edit {
          background: #e7f3ff;
          color: var(--theme-color, #0088cc);
        }

        .btn-edit:hover {
          background: var(--theme-color, #0088cc);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-delete {
          background: #fee;
          color: #dc3545;
        }

        .btn-delete:hover {
          background: #dc3545;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Modal */
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1040;
          backdrop-filter: blur(3px);
        }

        .modal-dialog-custom {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 90%;
          max-width: 900px;
          max-height: 90vh;
          z-index: 1050;
          animation: slideIn 0.3s ease;
        }

        .modal-content-custom {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
          max-height: 90vh;
        }

        .modal-header-custom {
          padding: 15px 20px;
          /* Dynamic BG */
          background: var(--theme-color, #0088cc);
          
          border-bottom: 3px solid #00d4aa;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header-custom h5 {
          margin: 0;
          color: white;
          font-size: 16px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .btn-close-modal {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-close-modal:hover {
          background: white;
          color: var(--theme-color, #0088cc);
        }

        .modal-body-custom {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
        }

        .modal-footer-custom {
          padding: 15px 20px;
          border-top: 2px solid #e0e6ed;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .btn-cancel, .btn-save {
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-cancel {
          background: #6c757d;
          color: white;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #5a6268;
          transform: translateY(-2px);
        }

        .btn-save {
          background: #28a745;
          color: white;
        }

        .btn-save:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
        }

        .btn-cancel:disabled, .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .company-header {
            flex-direction: column;
            gap: 12px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .form-group.flex-2 {
            grid-column: span 1;
          }

          .toolbar-actions {
            flex-direction: column;
            width: 100%;
          }

          .search-box {
            width: 100%;
          }

          .modal-dialog-custom {
            width: 95%;
          }

          .companies-table {
            font-size: 11px;
          }

          .companies-table thead th,
          .companies-table tbody td {
            padding: 8px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default PharmaCompanyForm;
